import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Trash2 } from "lucide-react";
import { LockToggle } from "./sections/LockToggle";

interface WalletManagerProps {
  wallets: string[];
  onChange: (wallets: string[]) => void;
  maxWallets?: number;
  type: "marketing" | "development";
  label?: string;
  locked?: boolean;
  onLockToggle?: (locked: boolean) => void;
}

export function WalletManager({ 
  wallets, 
  onChange, 
  maxWallets = 10, 
  type,
  label,
  locked,
  onLockToggle 
}: WalletManagerProps) {
  const [error, setError] = useState<string | null>(null);

  const addWallet = () => {
    if (wallets.length >= maxWallets) {
      setError(`Only ${maxWallets} wallet allowed`);
      return;
    }
    onChange([...wallets, ""]);
    setError(null);
  };

  const removeWallet = (index: number) => {
    const newWallets = wallets.filter((_, i) => i !== index);
    onChange(newWallets);
    setError(null);
  };

  const updateWallet = (index: number, value: string) => {
    const newWallets = [...wallets];
    newWallets[index] = value;
    onChange(newWallets);
  };

  return (
    <div className="space-y-3">
      {label && (
        <div className="flex items-center justify-between">
          <span>{label}</span>
          {onLockToggle && (
            <LockToggle
              locked={locked || false}
              onToggle={onLockToggle}
              tooltip={`If locked, ${type} wallet settings cannot be changed after token deployment`}
            />
          )}
        </div>
      )}
      
      {wallets.map((wallet, index) => (
        <div key={index} className="flex gap-2">
          <div className="bg-white/10 flex-1">
            <Input
              placeholder={`Enter ${type} wallet address`}
              value={wallet}
              onChange={(e) => updateWallet(index, e.target.value)}
              className="border-0 bg-transparent"
            />
          </div>
          {wallets.length > 1 && (
            <Button
              variant="ghost"
              size="icon"
              onClick={() => removeWallet(index)}
              className="h-10 w-10 text-red-500 hover:text-red-600 hover:bg-red-500/10"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          )}
        </div>
      ))}
      
      {error && <p className="text-sm text-red-500">{error}</p>}
      
      {wallets.length === 0 && (
        <Button
          variant="outline"
          size="sm"
          onClick={addWallet}
          className="w-full bg-gradient-to-r from-white via-yellow-500 to-gray-300 hover:from-gray-300 hover:via-yellow-600 hover:to-white text-black dark:from-black dark:via-yellow-500 dark:to-gray-800 dark:hover:from-gray-800 dark:hover:via-yellow-600 dark:hover:to-black dark:text-white"
        >
          Add {type === "marketing" ? "Marketing" : "Development"} Wallet
        </Button>
      )}
    </div>
  );
}